import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError, formatRelative } from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Search, Lock, SlidersHorizontal, X, Save, Trash2, Star, Send } from "lucide-react";
import CountrySelect from "../components/CountrySelect";
import CountryFlag from "../components/CountryFlag";
import AdminBroadcastModal from "../components/AdminBroadcastModal";
import UserBadges from "../components/UserBadges";
import PostSearchPanel from "../components/search/PostSearchPanel";

/**
 * Kjønn-symbol som SVG. Brukes ved siden av alder på søke-cards.
 * Farger respekterer site-paletten (magenta = kvinne, blå = mann, lilla = trans, gull = par, grå = annet).
 * Par-undertyper (hetero/gay/lesbe) viser to symboler ved siden av hverandre.
 */
function GenderSymbol({ gender, coupleType }) {
  const g = (gender || "").toLowerCase();
  // Par-undertyper overstyrer det generelle par-symbolet
  if (g === "par" && coupleType) {
    const ct = String(coupleType).toLowerCase();
    if (ct === "hetero") {
      return (
        <span title="Hetero-par" data-testid="gender-couple-hetero" aria-label="Hetero-par"
              style={{ fontSize: "13px", lineHeight: 1, fontWeight: 700 }}>
          <span style={{ color: "#4A90E2" }}>♂</span>
          <span style={{ color: "#E91E63" }}>♀</span>
        </span>
      );
    }
    if (ct === "gay") {
      return (
        <span title="Gay-par" data-testid="gender-couple-gay" aria-label="Gay-par"
              style={{ color: "#4A90E2", fontSize: "13px", lineHeight: 1, fontWeight: 700 }}>
          ♂♂
        </span>
      );
    }
    if (ct === "lesbe") {
      return (
        <span title="Lesbisk par" data-testid="gender-couple-lesbe" aria-label="Lesbisk par"
              style={{ color: "#E91E63", fontSize: "13px", lineHeight: 1, fontWeight: 700 }}>
          ♀♀
        </span>
      );
    }
  }
  const map = {
    kvinne: { sym: "♀", color: "#E91E63", label: "Kvinne" },
    mann:   { sym: "♂", color: "#4A90E2", label: "Mann" },
    par:    { sym: "⚭", color: "#D4A574", label: "Par" },
    trans:  { sym: "⚧", color: "#9C27B0", label: "Trans" },
    annet:  { sym: "⚪", color: "#9E9E9E", label: "Annet" },
  };
  const info = map[g];
  if (!info) return null;
  return (
    <span
      title={info.label}
      style={{ color: info.color, fontSize: "13px", lineHeight: 1, fontWeight: 700 }}
      data-testid={`gender-${g}`}
      aria-label={info.label}
    >
      {info.sym}
    </span>
  );
}

const GENDERS = (t) => [
  { k: "kvinne", label: t("search_page.women") },
  { k: "mann",   label: t("search_page.men") },
  { k: "par",    label: t("search_page.couples") },
  { k: "trans",  label: t("search_page.trans") },
  { k: "annet",  label: t("search_page.other") },
];
const AGE_BANDS = [
  { k: "18-30", label: "18–30" },
  { k: "31-45", label: "31–45" },
  { k: "45+",   label: "45+" },
];
const SORTS_FREE = (t) => [
  { k: "random", label: t("search.sort_random", "Tilfeldig") },
];
const SORTS_VIP = (t) => [
  { k: "recent_active",  label: t("search_page.sort_last_active") },
  { k: "newest",         label: t("search.sort_newest") },
  { k: "oldest_member",  label: t("search.sort_oldest_member", "Eldste medlemmer") },
  { k: "alphabetical",   label: t("search.sort_alphabetical", "Alfabetisk (A–Å)") },
  { k: "random",         label: t("search.sort_random", "Tilfeldig") },
];
const LAST_ACTIVE_VIP = (t) => [
  { k: "now",   label: t("search_page.active_now") },
  { k: "30min", label: t("search_page.last_half_hour") },
  { k: "24h",   label: t("search_page.last_24h") },
  { k: "7d",    label: t("search_page.last_7d") },
  { k: "30d",   label: t("search_page.last_30d") },
  { k: "any",   label: t("search_page.anytime") },
];

function Pill({ active, onClick, children, locked, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`font-meta inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-sm transition-colors text-[10px] ${
        locked
          ? "border-[var(--cognac)]/40 text-[var(--cognac)] bg-[var(--cognac)]/5 cursor-not-allowed"
          : active
            ? "border-[var(--magenta)] bg-[var(--magenta)] text-black"
            : "border-white/15 text-[var(--cream-soft)] hover:border-white/40 hover:text-[var(--cream)]"
      }`}
    >
      {locked && <Lock className="h-2.5 w-2.5" />}
      {children}
    </button>
  );
}

function VipLock({ children }) {
  return (
    <div className="relative">
      <div className="opacity-60 pointer-events-none">{children}</div>
      <Link
        to="/oppgrader"
        className="absolute inset-0 flex items-center justify-center gap-2 bg-[var(--noir)]/85 hover:bg-[var(--noir)]/70 transition-colors font-meta text-[10px] text-[var(--cognac)]"
      >
        <Lock className="h-3 w-3" /> Krever VIP
      </Link>
    </div>
  );
}

export default function Sok() {
  const { t } = useTranslation();
  const { user } = useAuth();

  // sessionStorage-persistert filter-state (beholdes ved profil-navigasjon, nullstilles ved ny tab / hard-refresh)
  const SS_KEY = "sok_filter_v1";
  const loadPersisted = () => {
    try {
      const raw = sessionStorage.getItem(SS_KEY);
      if (!raw) return {};
      return JSON.parse(raw) || {};
    } catch { return {}; }
  };
  const persisted = useMemo(loadPersisted, []);

  const [searchMode, setSearchMode] = useState(persisted.searchMode || "profiles"); // profiles | ads
  const isVip = useMemo(
    () => user && ["vip", "creator", "premium", "pluss"].includes((user.tier || "free").toLowerCase()),
    [user]
  );

  // FREE
  const [gender, setGender] = useState(new Set(persisted.gender || []));
  const [country, setCountry] = useState(persisted.country ?? null);  // ISO 3166-1 alpha-2; null = alle land
  const [fylke, setFylke] = useState(persisted.fylke || "");
  const [ageBand, setAgeBand] = useState(persisted.ageBand || "");
  const [onlineNow, setOnlineNow] = useState(!!persisted.onlineNow);
  const [hasAvatar, setHasAvatar] = useState(!!persisted.hasAvatar);
  const [sort, setSort] = useState(persisted.sort || "random");

  // VIP
  const [text, setText] = useState(persisted.text || "");
  const [city, setCity] = useState(persisted.city || "");
  const [lastActive, setLastActive] = useState(persisted.lastActive || "");
  const [minAge, setMinAge] = useState(persisted.minAge ?? 18);
  const [maxAge, setMaxAge] = useState(persisted.maxAge ?? 99);
  const [tags, setTags] = useState(persisted.tags || "");

  // Persist alle filter-endringer
  useEffect(() => {
    try {
      sessionStorage.setItem(SS_KEY, JSON.stringify({
        searchMode,
        gender: [...gender],
        country, fylke, ageBand, onlineNow, hasAvatar, sort,
        text, city, lastActive, minAge, maxAge, tags,
      }));
    } catch { /* quota / private mode — ignorer */ }
  }, [searchMode, gender, country, fylke, ageBand, onlineNow, hasAvatar, sort, text, city, lastActive, minAge, maxAge, tags]);

  // Lokasjoner
  const [locations, setLocations] = useState({ countries: [], top_countries: ["NO","SE","DK","GB","US"], subdivisions: {}, fylker: [], cities_by_fylke: {} });
  useEffect(() => {
    api.get("/search/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, []);
  // Aktive fylker/regioner basert på valgt land
  const activeSubdivisions = useMemo(() => {
    if (country && locations.subdivisions && locations.subdivisions[country]) {
      return locations.subdivisions[country];
    }
    if (!country) return locations.fylker || [];  // backward compat: vis norske fylker hvis intet land valgt
    return [];
  }, [country, locations]);
  // Reset fylke når land endres (men ikke ved første mount — bevar persistert verdi)
  const firstMountCountry = useRef(true);
  useEffect(() => {
    if (firstMountCountry.current) { firstMountCountry.current = false; return; }
    setFylke(""); setCity("");
  }, [country]);

  // Resultater
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Admin masse-DM (Broadcast) – kun owner
  const isOwner = user && user.role === "owner";
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  // Bygger filter-objektet som matcher backend BroadcastFilter
  const broadcastFilter = useMemo(() => {
    const f = {};
    if (gender.size) f.gender = [...gender].join(",");
    if (country) f.country = country;
    if (fylke) f.fylke = fylke;
    if (city) f.city = city;
    if (onlineNow) f.online_now = true;
    if (hasAvatar) f.has_avatar = true;
    // Bare send aldersfilter hvis bruker har endret det fra default (18–99)
    if (isVip && (Number(minAge) > 18 || Number(maxAge) < 99)) {
      f.age_min = Number(minAge);
      f.age_max = Number(maxAge);
    }
    return f;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gender, country, fylke, city, onlineNow, hasAvatar, minAge, maxAge, isVip]);

  const buildParams = () => {
    const p = new URLSearchParams();
    if (gender.size) p.set("gender", [...gender].join(","));
    if (country) p.set("country", country);
    if (fylke) p.set("fylke", fylke);
    if (ageBand) p.set("age_band", ageBand);
    if (onlineNow) p.set("online_now", "true");
    if (hasAvatar) p.set("has_avatar", "true");
    if (sort) p.set("sort", sort);
    if (isVip) {
      if (text.trim()) p.set("text", text.trim());
      if (city) p.set("city", city);
      if (lastActive) p.set("last_active", lastActive);
      if (minAge && minAge !== 18) p.set("min_age", String(minAge));
      if (maxAge && maxAge !== 99) p.set("max_age", String(maxAge));
      if (tags.trim()) p.set("tags", tags.trim());
    }
    p.set("page", String(page));
    p.set("per_page", "24");
    return p;
  };

  const search = async (pg = 1) => {
    setLoading(true);
    setError("");
    setPage(pg);
    try {
      const p = buildParams();
      if (pg !== page) p.set("page", String(pg));
      const r = await api.get(`/search/members?${p.toString()}`);
      setResults(r.data.results || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Initial søk – sett riktig default-sort per tier
  useEffect(() => {
    if (isVip && sort === "random") setSort("recent_active");
    search(1);
    /* eslint-disable-next-line */
  }, []);

  const resetAll = () => {
    setGender(new Set());
    setCountry(null);
    setFylke(""); setAgeBand(""); setOnlineNow(false); setHasAvatar(false);
    setSort(isVip ? "recent_active" : "random");
    setText(""); setCity(""); setLastActive(""); setMinAge(18); setMaxAge(99); setTags("");
    setTimeout(() => search(1), 0);
  };

  const toggleGender = (g) => {
    setGender((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g); else next.add(g);
      return next;
    });
  };

  // Lagrede søk (VIP)
  const [saved, setSaved] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState("");
  // Mobil: bottom-sheet filter
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Antall aktive filtre (for badge)
  const activeFilterCount = (
    (gender.size > 0 ? 1 : 0) +
    (country ? 1 : 0) +
    (fylke ? 1 : 0) +
    (city ? 1 : 0) +
    (ageBand ? 1 : 0) +
    (onlineNow ? 1 : 0) +
    (lastActive ? 1 : 0) +
    (hasAvatar ? 1 : 0) +
    (text.trim() ? 1 : 0) +
    (tags.trim() ? 1 : 0) +
    ((minAge !== 18 || maxAge !== 99) ? 1 : 0)
  );
  const loadSaved = () => {
    if (!isVip) return;
    api.get("/search/saved").then((r) => setSaved(r.data.results || [])).catch(() => {});
  };
  useEffect(() => { loadSaved(); /* eslint-disable-next-line */ }, [isVip]);

  const doSave = async () => {
    if (!saveName.trim()) return;
    try {
      const filters = Object.fromEntries(buildParams().entries());
      await api.post("/search/saved", { name: saveName.trim(), filters });
      setSaveName(""); setShowSaveDialog(false);
      loadSaved();
    } catch (e) { setError(formatApiError(e)); }
  };

  const loadSavedSearch = (s) => {
    const f = s.filters || {};
    setGender(new Set((f.gender || "").split(",").filter(Boolean)));
    setFylke(f.fylke || ""); setAgeBand(f.age_band || "");
    setOnlineNow(f.online_now === "true");
    setHasAvatar(f.has_avatar === "true");
    setSort(f.sort || (isVip ? "recent_active" : "random"));
    setText(f.text || ""); setCity(f.city || ""); setLastActive(f.last_active || "");
    setMinAge(Number(f.min_age) || 18); setMaxAge(Number(f.max_age) || 99);
    setTags(f.tags || "");
    setTimeout(() => search(1), 0);
  };

  const deleteSaved = async (id) => {
    if (!window.confirm("Slett lagret søk?")) return;
    try { await api.delete(`/search/saved/${id}`); loadSaved(); }
    catch (e) { setError(formatApiError(e)); }
  };

  const totalPages = Math.max(1, Math.ceil(total / 24));
  const availableCities = (locations.cities_by_fylke || {})[fylke] || [];

  return (
    <Layout withSidebar={false}>
      <h1 className="font-fraunces italic text-2xl sm:text-4xl lg:text-5xl text-[var(--cream)] leading-tight mb-3">
        {t("search_page.title")}.
      </h1>

      {!user && (
        <div className="panel-card p-8 my-6 text-center" data-testid="search-login-required">
          <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] mb-2">LÅST · 18+</div>
          <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-3">Søk krever innlogging</h2>
          <p className="text-[var(--cream-soft)] mb-5 max-w-md mx-auto">
            For å søke i medlems- og annonsedatabasen må du være registrert.
            Det tar 30 sekunder — og er helt gratis.
          </p>
          <a href="/?tab=register" className="btn-magenta inline-block px-6 py-2.5" data-testid="search-register-cta">
            Bli med gratis
          </a>
          <div className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-faint)] mt-4">
            HAR DU KONTO?{" "}
            <a href="/?tab=login" className="text-[var(--magenta)] hover:text-[var(--cream)]">LOGG INN</a>
          </div>
        </div>
      )}

      {user && (<>
      {/* Segmented control + handlinger på én linje */}
      <div className="flex items-center flex-wrap gap-3 mb-6">
        <div className="inline-flex border border-white/15 rounded-sm overflow-hidden" data-testid="search-mode-toggle">
          <button
            onClick={() => setSearchMode("profiles")}
            className={`font-meta px-4 py-2 transition-colors ${
              searchMode === "profiles" ? "bg-[var(--magenta)] text-black" : "text-[var(--cream-soft)] hover:text-[var(--cream)]"
            }`}
            data-testid="mode-profiles"
          >
            {t("search_page.tab_profiles")}
          </button>
          <button
            onClick={() => setSearchMode("ads")}
            className={`font-meta px-4 py-2 border-l border-white/15 transition-colors ${
              searchMode === "ads" ? "bg-[var(--magenta)] text-black" : "text-[var(--cream-soft)] hover:text-[var(--cream)]"
            }`}
            data-testid="mode-ads"
          >
            {t("search_page.tab_ads")}
          </button>
          <button
            onClick={() => setSearchMode("posts")}
            className={`font-meta px-4 py-2 border-l border-white/15 transition-colors ${
              searchMode === "posts" ? "bg-[var(--magenta)] text-black" : "text-[var(--cream-soft)] hover:text-[var(--cream)]"
            }`}
            data-testid="mode-posts"
          >
            {t("search_page.tab_posts", "Innlegg")}
          </button>
        </div>

        {searchMode === "profiles" && (
          <div className="hidden sm:flex items-center gap-2 ml-auto flex-wrap">
            <span className="font-meta text-[10px] text-[var(--cream-soft)] whitespace-nowrap mr-1">
              {t("search_page.hits", { count: total })}
            </span>
            <button onClick={() => search(1)} disabled={loading} className="btn-magenta" data-testid="search-btn-top">
              <Search className="h-3.5 w-3.5 inline mr-2" /> {loading ? "…" : t("search_page.search_btn")}
            </button>
            <button onClick={resetAll} className="btn-ghost font-meta text-[10px]" data-testid="reset-btn-top">
              {t("search_page.reset_btn")}
            </button>
            {isVip && (
              <button onClick={() => setShowSaveDialog(true)} className="btn-ghost font-meta text-[10px] inline-flex items-center gap-1.5" data-testid="save-search-btn-top">
                <Save className="h-3 w-3" /> {t("search_page.save_search")}
              </button>
            )}
          </div>
        )}
      </div>

      {searchMode === "posts" ? (
        <PostSearchPanel />
      ) : searchMode === "ads" ? (
        <AdsSearchPanel isVip={isVip} fylker={locations.fylker || []} />
      ) : (
      <>
      <p className="hidden sm:block font-manrope text-[var(--cream-soft)] max-w-2xl mb-8 text-sm leading-relaxed">
        {t("search_page.subtitle")}
        {!isVip && <> Avanserte filtre krever <Link to="/oppgrader" className="text-[var(--magenta)] hover:underline">VIP</Link>.</>}
      </p>

      {/* Lagrede søk (VIP) */}
      {isVip && saved.length > 0 && (
        <div className="mb-6 panel-galleri" style={{ padding: "12px 18px" }} data-testid="saved-searches">
          <div className="font-meta mb-2 inline-flex items-center gap-1.5"><Star className="h-3 w-3" /> Lagrede søk</div>
          <div className="flex flex-wrap gap-2">
            {saved.map((s) => (
              <div key={s.id} className="flex items-center border border-white/15 rounded-sm overflow-hidden">
                <button onClick={() => loadSavedSearch(s)} className="font-meta px-3 py-1 text-[var(--cream)] hover:bg-white/5">
                  {s.name}
                </button>
                <button onClick={() => deleteSaved(s.id)} className="px-2 py-1 border-l border-white/15 text-[var(--cream-faint)] hover:text-[var(--magenta)]" aria-label="Slett">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MOBIL: kompakt filter-bar */}
      <div className="sm:hidden mb-4 flex items-center gap-2" data-testid="mobile-filter-bar">
        <button
          onClick={() => setMobileFiltersOpen(true)}
          className="btn-ghost flex-1 flex items-center justify-center gap-2 py-2"
          data-testid="mobile-open-filters"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          <span className="font-meta text-[11px]">Filtre</span>
          {activeFilterCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold bg-[var(--magenta)] text-white rounded-full">
              {activeFilterCount}
            </span>
          )}
        </button>
        <button
          onClick={() => search(1)}
          disabled={loading}
          className="btn-magenta flex items-center gap-1.5 py-2 px-4"
          data-testid="mobile-search-btn"
        >
          <Search className="h-3.5 w-3.5" />
          <span className="font-meta text-[11px]">{loading ? "…" : "Søk"}</span>
        </button>
        {activeFilterCount > 0 && (
          <button
            onClick={resetAll}
            className="btn-ghost py-2 px-3"
            data-testid="mobile-reset-btn"
            aria-label="Nullstill"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Filter-panel – 3-kolonne grid: [Kjønn+Alder] [Land/Fylke/By] [Sist aktiv+Sortering]
          MOBIL: rendres som bottom-sheet når mobileFiltersOpen=true, ellers skjult.
          DESKTOP (sm+): alltid synlig inline. */}
      {mobileFiltersOpen && (
        <div
          className="sm:hidden fixed inset-0 z-50 bg-black/70"
          onClick={() => setMobileFiltersOpen(false)}
          data-testid="mobile-filter-backdrop"
        />
      )}
      <div
        className={`panel-galleri space-y-3 mb-6 ${
          mobileFiltersOpen
            ? "fixed inset-x-0 bottom-0 top-16 z-50 overflow-y-auto rounded-t-lg"
            : "hidden"
        } sm:!relative sm:!inset-auto sm:!z-auto sm:!block sm:!rounded-none sm:!overflow-visible`}
        style={{ padding: "14px 18px" }}
        data-testid="filter-panel"
      >
        {/* MOBIL: header + lukkeknapp */}
        <div className="sm:hidden flex items-center justify-between pb-3 border-b border-white/10 mb-3 sticky top-0 bg-[var(--noir-elev)] -mx-[18px] px-[18px] pt-1 z-10">
          <span className="font-fraunces italic text-lg text-[var(--cream)]">Filtre</span>
          <button
            onClick={() => setMobileFiltersOpen(false)}
            className="btn-ghost p-1.5"
            aria-label="Lukk"
            data-testid="mobile-close-filters"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* === KOLONNE 1: Kjønn + Alder === */}
          <div className="space-y-5">
            <div>
              <div className="font-meta mb-2">
                {t("search_page.gender")}{" "}
                <span className="normal-case tracking-normal text-[10px] text-[var(--cream-faint)] italic">
                  · {t("search_page.gender_help")}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {GENDERS(t).map((g) => (
                  <Pill key={g.k} active={gender.has(g.k)} onClick={() => toggleGender(g.k)} testId={`gender-${g.k}`}>
                    {t(`search_page.${ {kvinne:'women',mann:'men',par:'couples',trans:'trans',annet:'other'}[g.k] }`)}
                  </Pill>
                ))}
              </div>
            </div>
            <div>
              <div className="font-meta mb-2">
                {t("search_page.age")}
                {isVip && (
                  <span className="normal-case tracking-normal text-[10px] text-[var(--cream-faint)] italic ml-1">
                    · {t("search_page.age_help")}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {AGE_BANDS.map((b) => (
                  <Pill key={b.k} active={ageBand === b.k} onClick={() => setAgeBand(ageBand === b.k ? "" : b.k)} testId={`age-${b.k}`}>
                    {b.label}
                  </Pill>
                ))}
              </div>
              {isVip && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="font-meta text-[10px] text-[var(--cream-soft)]">{t("search_page.from")}</span>
                  <input type="number" min="18" max="99" value={minAge} onChange={(e) => setMinAge(+e.target.value || 18)}
                         className="input-editorial w-20" data-testid="min-age-input" />
                  <span className="font-meta text-[10px] text-[var(--cream-soft)]">{t("search_page.to")}</span>
                  <input type="number" min="18" max="99" value={maxAge} onChange={(e) => setMaxAge(+e.target.value || 99)}
                         className="input-editorial w-20" data-testid="max-age-input" />
                  <span className="font-meta text-[10px] text-[var(--cream-soft)]">{t("search_page.years")}</span>
                </div>
              )}
            </div>
          </div>

          {/* === KOLONNE 2: Land + Fylke + By/sted (hierarkisk) === */}
          <div className="space-y-3">
            <div>
              <div className="font-meta mb-2">{t("country_select.label", "Land")}</div>
              <CountrySelect
                value={country}
                onChange={setCountry}
                allCountries={locations.countries || []}
                topCountries={locations.top_countries || ["NO","SE","DK","GB","US"]}
                testIdPrefix="country-filter"
              />
            </div>
            <div>
              <div className="font-meta mb-2">
                {country === "SE" ? t("country_select.fylke_se")
                  : country === "DK" ? t("country_select.fylke_dk")
                  : country === "NO" || !country ? t("country_select.fylke_no")
                  : t("country_select.fylke_other")}
              </div>
              <select
                value={fylke}
                onChange={(e) => { setFylke(e.target.value); setCity(""); }}
                disabled={activeSubdivisions.length === 0}
                className="input-editorial w-full disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="fylke-select"
              >
                <option value="">
                  {activeSubdivisions.length > 0
                    ? t("search_page.all_fylker")
                    : t("country_select.fylke_other")}
                </option>
                {activeSubdivisions.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <div className="font-meta mb-2">
                {t("country_select.city")} {!isVip && <Lock className="h-2.5 w-2.5 inline ml-1 text-[var(--cognac)]" />}
              </div>
              {isVip ? (
                <select value={city} onChange={(e) => setCity(e.target.value)}
                        disabled={!fylke || activeSubdivisions.length === 0}
                        className="input-editorial w-full disabled:opacity-50" data-testid="city-select">
                  <option value="">{fylke ? t("gallery.filter_all") : t("search_page.select_fylke_first")}</option>
                  {availableCities.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              ) : (
                <select disabled className="input-editorial w-full opacity-50 cursor-not-allowed">
                  <option>VIP</option>
                </select>
              )}
            </div>
          </div>

          {/* === KOLONNE 3: Sist aktiv + Sortering === */}
          <div className="space-y-5">
            <div>
              <div className="font-meta mb-2">{t("search_page.last_active")}</div>
              {isVip ? (
                <div className="flex flex-wrap gap-1.5">
                  <Pill active={onlineNow} onClick={() => { setOnlineNow(!onlineNow); if (!onlineNow) setLastActive(""); }} testId="online-now-vip">
                    {t("search_page.online_now")}
                  </Pill>
                  {LAST_ACTIVE_VIP(t).map((l) => (
                    <Pill
                      key={l.k}
                      active={lastActive === l.k}
                      onClick={() => { setLastActive(lastActive === l.k ? "" : l.k); if (onlineNow) setOnlineNow(false); }}
                      testId={`la-${l.k}`}
                    >
                      {l.label}
                    </Pill>
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  <Pill active={onlineNow} onClick={() => setOnlineNow(!onlineNow)} testId="online-now">
                    {t("search_page.online_now")}
                  </Pill>
                  <Pill locked testId="la-locked-vip">VIP</Pill>
                </div>
              )}
            </div>
            <div>
              <div className="font-meta mb-2">{t("search_page.sort")}</div>
              <select value={sort} onChange={(e) => setSort(e.target.value)} disabled={!isVip}
                      className="input-editorial w-full disabled:opacity-60 disabled:cursor-not-allowed" data-testid="sort-select">
                {(isVip ? SORTS_VIP(t) : SORTS_FREE(t)).map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
              </select>
              {!isVip && (
                <div className="font-meta text-[10px] text-[var(--cream-faint)] italic mt-1 inline-flex items-center gap-1">
                  <Lock className="h-2.5 w-2.5 text-[var(--cognac)]" /> Flere sorteringer for VIP
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Linje 3 – Tekstfelt + tagger (kompakt) */}
        <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-white/10">
          {isVip ? (
            <Pill active={hasAvatar} onClick={() => setHasAvatar(!hasAvatar)} testId="has-avatar">
              {t("search.has_profile_pic")}
            </Pill>
          ) : (
            <Pill locked testId="has-avatar-locked">{t("search.has_profile_pic")} (VIP)</Pill>
          )}
          {isVip && (
            <div className="flex-1 min-w-[200px] flex items-center gap-2">
              <Search className="h-3.5 w-3.5 text-[var(--cream-faint)]" />
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("search.search_username_bio")}
                     className="input-editorial flex-1" data-testid="text-search" />
            </div>
          )}
          {isVip && (
            <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder={t("search.tags_placeholder")}
                   className="input-editorial w-48" data-testid="tag-search" />
          )}
        </div>

        {/* VIP-only avanserte filtre (kompakt liste, klikkbar for VIP) */}
        {!isVip && (
          <details className="border-t border-white/10 pt-3">
            <summary className="font-meta cursor-pointer inline-flex items-center gap-2 text-[var(--cognac)] hover:text-[var(--magenta)]">
              <SlidersHorizontal className="h-3 w-3" /> Avansert søk – krever VIP
            </summary>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-[var(--cream-soft)] font-meta text-[10px]">
              {["Alder-slider","Sist aktiv","By","Tekst-søk","Tagger","Kroppstype","Sivilstatus","Etnisitet","Som søker","Som ikke søker","Røyking/alkohol","Tatovering/piercing","Gjensidighet","Lagrede søk"].map((t) => (
                <div key={t} className="inline-flex items-center gap-1.5 px-2 py-1 border border-[var(--cognac)]/30 rounded-sm">
                  <Lock className="h-2.5 w-2.5 text-[var(--cognac)]" /> {t}
                </div>
              ))}
            </div>
          </details>
        )}

        {/* MOBIL: sticky "Bruk filtre"-knapp nederst i bottom-sheet */}
        <div className="sm:hidden sticky bottom-0 -mx-[18px] px-[18px] pt-3 pb-4 bg-[var(--noir-elev)] border-t border-white/10 flex gap-2">
          <button
            onClick={resetAll}
            className="btn-ghost flex-1 py-2.5"
            data-testid="mobile-sheet-reset"
          >
            <span className="font-meta text-[11px]">Nullstill</span>
          </button>
          <button
            onClick={() => { search(1); setMobileFiltersOpen(false); }}
            disabled={loading}
            className="btn-magenta flex-1 py-2.5 inline-flex items-center justify-center gap-1.5"
            data-testid="mobile-sheet-apply"
          >
            <Search className="h-3.5 w-3.5" />
            <span className="font-meta text-[11px]">
              {loading ? "…" : `Vis ${total} treff`}
            </span>
          </button>
        </div>
      </div>

      {showSaveDialog && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={() => setShowSaveDialog(false)}>
          <div className="bg-[var(--noir-elev)] border border-white/10 rounded-sm p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-fraunces italic text-2xl text-[var(--cream)] mb-3">Lagre søk</h3>
            <input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Navn på søket…"
                   className="input-editorial w-full mb-4" autoFocus data-testid="save-name-input" />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowSaveDialog(false)} className="btn-ghost text-xs">Avbryt</button>
              <button onClick={doSave} disabled={!saveName.trim()} className="btn-magenta text-xs" data-testid="save-confirm">Lagre</button>
            </div>
          </div>
        </div>
      )}

      {error && <div className="text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3 text-sm mb-4">{error}</div>}

      {/* Admin: Send masse-DM til alle som matcher filtret */}
      {isOwner && total > 0 && (
        <div className="flex items-center justify-end mb-4">
          <button
            onClick={() => setBroadcastOpen(true)}
            className="px-4 py-2 border border-[var(--magenta)] text-[var(--magenta)] font-meta text-xs uppercase tracking-[0.15em] hover:bg-[var(--magenta)] hover:text-black inline-flex items-center gap-2"
            data-testid="open-broadcast-modal"
          >
            <Send className="h-3.5 w-3.5" />
            Send DM til disse {total} brukerne
          </button>
        </div>
      )}

      {/* Resultater */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4" data-testid="results-grid">
        {results.map((r) => (
          <Link key={r.id} to={`/profile/${r.username}`} className="group relative bg-black border border-white/10 hover:border-[var(--magenta)]/60 transition-colors" data-testid={`result-${r.username}`}>
            <div className="aspect-[3/4] bg-black flex items-center justify-center">
              {r.avatar_url ? (
                <img src={r.avatar_url} alt={r.username} className="w-full h-full object-cover" />
              ) : (
                <Avatar className="h-20 w-20">
                  <AvatarFallback className="bg-[var(--magenta)] text-black text-2xl font-bold">
                    {(r.username || "?").slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              )}
              {r.online && (
                <span className="absolute top-2 right-2 h-2.5 w-2.5 rounded-full bg-[var(--magenta)] ring-2 ring-black" title="Online nå" />
              )}
              {r.is_creator && (
                <span className="absolute top-2 left-2 font-meta text-[9px] px-1.5 py-0.5 bg-[var(--cognac)]/90 text-black">
                  CREATOR
                </span>
              )}
            </div>
            <div className="p-3">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-fraunces italic text-base text-[var(--cream)] truncate group-hover:text-[var(--magenta)] inline-flex items-center gap-1 min-w-0">
                  <span className="truncate">{r.username}</span>
                  <UserBadges user={r} size="sm" />
                </span>
                <span className="font-meta text-[10px] shrink-0 inline-flex items-center gap-1" data-testid={`age-gender-${r.username}`}>
                  {r.gender === "par" && (r.partner_a_age || r.partner_b_age) ? (
                    <>
                      {r.partner_a_age && <span data-testid={`age-a-${r.username}`}>{r.partner_a_age}</span>}
                      {r.partner_a_gender && <GenderSymbol gender={r.partner_a_gender} />}
                      {r.partner_b_age && <span data-testid={`age-b-${r.username}`}>{r.partner_b_age}</span>}
                      {r.partner_b_gender && <GenderSymbol gender={r.partner_b_gender} />}
                    </>
                  ) : (
                    <>
                      {r.age && <span>{r.age}</span>}
                      {r.gender && <GenderSymbol gender={r.gender} coupleType={r.couple_type} />}
                    </>
                  )}
                </span>
              </div>
              <div className="font-meta text-[9px] truncate mt-0.5 flex items-center gap-1.5">
                {r.country && (
                  <CountryFlag iso={r.country} size="sm" data-testid={`flag-${r.username}`} />
                )}
                <span className="truncate">{[r.city, r.fylke].filter(Boolean).join(", ") || "–"}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {results.length === 0 && !loading && (
        <div className="text-center py-16 font-fraunces italic text-[var(--cream-soft)]">
          {t("ads_search.no_hits_filters")}
        </div>
      )}

      {/* Paginering */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8" data-testid="pagination">
          <button onClick={() => search(Math.max(1, page - 1))} disabled={page === 1}
                  className="btn-ghost text-xs disabled:opacity-30">{t("pagination.previous")}</button>
          <span className="font-meta">{t("pagination.page_of", { page, total: totalPages })}</span>
          <button onClick={() => search(Math.min(totalPages, page + 1))} disabled={page === totalPages}
                  className="btn-ghost text-xs disabled:opacity-30">{t("pagination.next")}</button>
        </div>
      )}
      </>
      )}

      {/* Admin: Masse-DM modal */}
      {isOwner && (
        <AdminBroadcastModal
          open={broadcastOpen}
          onClose={() => setBroadcastOpen(false)}
          filter={broadcastFilter}
        />
      )}
      </>)}
    </Layout>
  );
}


// =========================================================
// Annonse-søk: bruker samme grid-stil + sub-filtre
// =========================================================
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AdsSearchPanel({ isVip, fylker }) {
  const { t } = useTranslation();
  const ADS_SS_KEY = "ads_filter_v1";
  const adsInit = (() => {
    try { return JSON.parse(sessionStorage.getItem(ADS_SS_KEY) || "{}") || {}; }
    catch { return {}; }
  })();
  const [cats, setCats] = React.useState({ categories: [] });
  const [cat, setCat] = React.useState(adsInit.cat || "");
  const [sub, setSub] = React.useState(adsInit.sub || "");
  const [fylke, setFylke] = React.useState(adsInit.fylke || "");
  const [text, setText] = React.useState(adsInit.text || "");
  const [results, setResults] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    api.get("/ads/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  React.useEffect(() => {
    try { sessionStorage.setItem(ADS_SS_KEY, JSON.stringify({ cat, sub, fylke, text })); }
    catch { /* ignore quota */ }
  }, [cat, sub, fylke, text]);

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (cat) p.set("category", cat);
    if (sub) p.set("subcategory", sub);
    if (fylke) p.set("fylke", fylke);
    if (isVip && text.trim()) p.set("text", text.trim());
    p.set("per_page", "24");
    api.get(`/ads?${p.toString()}`)
      .then((r) => { setResults(r.data.results); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  };
  React.useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat, sub, fylke]);

  const catObj = cats.categories.find((c) => c.key === cat);

  return (
    <div>
      <p className="font-manrope text-[var(--cream-soft)] max-w-2xl mb-6 text-sm leading-relaxed">
        {t("ads_search.lead")}
      </p>

      <div className="panel-galleri grid grid-cols-1 md:grid-cols-4 gap-3 mb-6" style={{ padding: "16px 18px" }}>
        <select value={cat} onChange={(e) => { setCat(e.target.value); setSub(""); }} className="input-editorial" data-testid="ads-cat">
          <option value="">{t("ads_search.all_cats")}</option>
          {cats.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <select value={sub} onChange={(e) => setSub(e.target.value)} disabled={!catObj} className="input-editorial disabled:opacity-50" data-testid="ads-sub">
          <option value="">{catObj ? t("ads_search.all_subs") : t("ads_search.pick_cat")}</option>
          {catObj?.subs.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select value={fylke} onChange={(e) => setFylke(e.target.value)} className="input-editorial" data-testid="ads-fylke">
          <option value="">{t("ads_search.all_counties")}</option>
          {fylker.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        {isVip ? (
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                 placeholder={t("ads_search.text_placeholder")} className="input-editorial" data-testid="ads-text" />
        ) : (
          <div className="font-meta text-[10px] text-[var(--cognac)] inline-flex items-center gap-1.5 self-center">
            <Lock className="h-3 w-3" /> {t("ads_search.text_vip_required")}
          </div>
        )}
      </div>

      <div className="font-meta mb-3">{total === 1 ? t("ads_search.count_one", { count: total }) : t("ads_search.count_other", { count: total })}</div>

      {loading ? (
        <div className="font-fraunces italic text-[var(--cream-soft)]">{t("ads_search.loading")}</div>
      ) : results.length === 0 ? (
        <div className="text-center py-12 font-fraunces italic text-[var(--cream-soft)]">
          {t("ads_search.no_hits")}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4" data-testid="ads-results">
          {results.map((ad) => {
            const firstImg = ad.image_paths?.[0];
            return (
              <Link key={ad.id} to={`/annonser/se/${ad.id}`}
                    className="border border-white/10 hover:border-[var(--magenta)]/60 bg-black flex flex-col" data-testid={`ad-card-${ad.id}`}>
                <div className="aspect-[3/4] bg-black flex items-center justify-center overflow-hidden">
                  {firstImg ? (
                    <img src={`${BACKEND_URL}/api/ads/images/raw?path=${encodeURIComponent(firstImg)}`}
                         alt={ad.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="font-fraunces italic text-[var(--cream-faint)] text-xs">{t("ads_search.no_image")}</div>
                  )}
                </div>
                <div className="p-3 flex-1">
                  <div className="font-fraunces italic text-sm text-[var(--cream)] line-clamp-2 leading-tight">{ad.title}</div>
                  <div className="font-meta text-[9px] mt-1 truncate">{ad.subcategory_label || ""}</div>
                  {ad.price != null && <div className="font-fraunces italic text-base text-[var(--magenta)] mt-1">{ad.price} kr</div>}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

